import React, { useState, useEffect } from 'react';
import { canEditDeal, canUseQuickActions, canUseCommsButtons, isLimitedAgent } from '../config/roleAccess';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getTransactionForUser, getTransaction, deleteTransaction, duplicateTransaction, markTransactionLost } from '../services/transactionService';
import { syncTaskDates, syncTeamTaskDates, activateLiveListingTasks, closeMlsCheckTasks, setListingLiveDate, releaseClosedTasks } from '../services/transactionTasks';
import { sendSellerCreatedEmails } from '../services/listingNotifications';
import BuyerDetailSections from '../components/BuyerDetailSections';
import SellerDetailSections from '../components/SellerDetailSections';
import UnderContractSummary from '../components/UnderContractSummary';
import { otherAgentOf } from '../components/OtherAgentQuickText';
import { ArrowLeft, Edit, Trash2, X, Copy } from 'lucide-react';
import TransactionTasksList from '../components/TransactionTasksList';
import TransactionCommunications from '../components/TransactionCommunications';
import MarketingLinks from '../components/MarketingLinks';
import PropertyInfoSection from '../components/PropertyInfoSection';
import { updateTransaction } from '../services/transactionService';
import { DealColorPicker } from '../components/DealColorPicker';
import AutomatedEmailsToggle from '../components/AutomatedEmailsToggle';
import DealDocumentsSection from '../components/DealDocumentsSection';
import { createPhotosInTask } from '../services/photosInTask';
import LinkedContacts from '../components/LinkedContacts';
import AccessPanel from '../components/AccessPanel';
import { shareTransaction, transferTransaction } from '../services/transactionService';
import MessagesSection from '../components/MessagesSection';
import SavedCmas from '../components/cma/SavedCmas';

export default function TransactionDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, userRole } = useAuth();
  const location = useLocation();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [actionMode, setActionMode] = useState(location.state?.action || null); // null | 'menu' | 'lost' | 'delete'
  const [lostChoice, setLostChoice] = useState('');
  const [lostNote, setLostNote] = useState('');
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    // The role arrives a moment after sign-in. Loading before it does made co-agent, TC and Admin deals
    // show "not found", and a slower earlier load could overwrite a newer one. Wait for the role, and
    // ignore any load that is no longer the latest.
    let stale = false;
    const loadTransaction = async () => {
      if (!user || !userRole) return;
      try {
        setLoading(true);
        setError(null);
        const data = await getTransactionForUser(user.uid, id, userRole);
        if (stale) return;
        if (data) {
          setTransaction(data);
        } else {
          setTransaction(null);
          setError('Transaction not found');
        }
      } catch (err) {
        if (stale) return;
        console.error('Error loading transaction:', err);
        setError('Failed to load transaction');
      } finally {
        if (!stale) setLoading(false);
      }
    };

    loadTransaction();
    return () => { stale = true; };
  }, [id, user?.uid, userRole]);

  const LOST_REASONS = [
    'Could not get financing',
    'Bad inspection',
    'Appraisal came in low',
    'Buyer backed out',
    'Seller backed out',
    'Other',
  ];

  const closeActions = () => {
    if (actionBusy) return;
    setActionMode(null);
    setLostChoice('');
    setLostNote('');
  };

  const handleDelete = async () => {
    try {
      setActionBusy(true);
      await deleteTransaction(user.uid, id);
      navigate('/transactions');
    } catch (err) {
      console.error('Error deleting transaction:', err);
      setError('Failed to delete transaction');
      setActionBusy(false);
    }
  };

  const handleMarkLost = async () => {
    if (!lostChoice) return;
    if (lostChoice === 'Other' && !lostNote.trim()) return;
    try {
      setActionBusy(true);
      const detail = lostNote.trim();
      await markTransactionLost(user.uid, id, { reason: lostChoice, detail });
      setTransaction({ ...transaction, status: 'lost', lostReason: lostChoice, lostDetail: detail, lostAt: new Date().toISOString() });
      setActionBusy(false);
      closeActions();
    } catch (err) {
      console.error('Error marking transaction lost:', err);
      setError('Could not mark this transaction as lost');
      setActionBusy(false);
    }
  };

  const handleDuplicate = async () => {
    try {
      setActionBusy(true);
      const newId = await duplicateTransaction(user.uid, transaction, id);
      navigate(`/transactions/${newId}`);
    } catch (err) {
      console.error('Error duplicating transaction:', err);
      setError('Could not duplicate this transaction');
      setActionBusy(false);
    }
  };

  const renderField = (label, value) => {
    if (!value) return null;
    return (
      <div className="detail-field-row" style={styles.fieldRow}>
        <label style={styles.fieldLabel}>{label}</label>
        <span style={styles.fieldValue}>{value}</span>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate('/transactions')} style={styles.backButton}>
            <ArrowLeft size={20} /> Back
          </button>
          <h1 style={styles.title}>Transaction Details</h1>
          <div style={{ width: '80px' }}></div>
        </div>
        <div style={styles.mainContent}>
          <p style={styles.loadingText}>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate('/transactions')} style={styles.backButton}>
            <ArrowLeft size={20} /> Back
          </button>
          <h1 style={styles.title}>Transaction Details</h1>
          <div style={{ width: '80px' }}></div>
        </div>
        <div style={styles.mainContent}>
          <p style={styles.errorText}>{error}</p>
        </div>
      </div>
    );
  }

  if (!transaction) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate('/transactions')} style={styles.backButton}>
            <ArrowLeft size={20} /> Back
          </button>
          <h1 style={styles.title}>Transaction Details</h1>
          <div style={{ width: '80px' }}></div>
        </div>
        <div style={styles.mainContent}>
          <p style={styles.errorText}>Transaction not found</p>
        </div>
      </div>
    );
  }

  const clientName = `${transaction.firstName} ${transaction.lastName}`;
  // Owner, Admin, TC and Marketing Manager get the deal actions; Local Assistant does not (see config/roleAccess).
  // People the deal is shared with can work it too; only the owner or an Admin can share or transfer it.
  const isOwner = canEditDeal(userRole, user.uid, transaction.ownerUid) || Boolean(transaction.sharedWith?.[user.uid]);
  const canManageDeal = userRole === 'Admin' || !transaction.ownerUid || transaction.ownerUid === user.uid;
  const accessPanel = (
    <AccessPanel what="transaction" meUid={user.uid} owner={transaction.ownerUid || user.uid} sharedWith={transaction.sharedWith || {}} canManage={canManageDeal}
      onShare={async (chg) => { await shareTransaction({ id, ...transaction, ownerUid: transaction.ownerUid || user.uid }, chg, user.uid); setTransaction(await getTransactionForUser(user.uid, id, userRole)); }}
      onTransfer={async (to, keep) => { await transferTransaction({ id, ...transaction, ownerUid: transaction.ownerUid || user.uid }, to, user.uid, { keepPrevious: keep }); const t = await getTransactionForUser(user.uid, id, userRole); if (t) setTransaction(t); else navigate('/transactions'); }} />
  );
  // Quick action: mark a seller listing Active (Live), the same way entering a live date does.
  const handleMarkActive = async () => {
    setActionBusy(true);
    try {
      const owner = transaction.ownerUid || user.uid;
      const name = user.displayName || user.email || 'Unknown';
      const liveAt = new Date().toISOString();
      await updateTransaction(owner, id, { status: 'live', listingStatus: 'live', liveAt });
      await activateLiveListingTasks(id);
      await closeMlsCheckTasks(id, name);
      setTransaction(prev => ({ ...prev, status: 'live', listingStatus: 'live', liveAt }));
      closeActions();
    } catch (err) {
      console.error('Could not mark active', err);
      window.alert('Could not mark this listing Active. Please try again.');
    } finally {
      setActionBusy(false);
    }
  };

  // Quick action: add "Photos are in" to the Marketing Manager's tasks for this listing.
  const handlePhotosIn = async () => {
    setActionBusy(true);
    try {
      const res = await createPhotosInTask(id, { address: transaction.propertyAddress, createdBy: user.uid });
      window.alert(res.created ? 'Added for the Marketing Manager: ' + res.name : 'That Photos are in task is already open for this listing.');
      closeActions();
    } catch (err) {
      console.error('Could not add photos task', err);
      window.alert('Could not add the Photos are in task. Please try again.');
    } finally {
      setActionBusy(false);
    }
  };


  const isSellerDeal = String(transaction.type || '').toLowerCase() === 'seller';
  const isDraftDeal = !!transaction.isDraft || transaction.status === 'draft';
  const hasUcDraft = !!transaction.underContractDraft && !transaction.underContract;
  const showUcButton = isSellerDeal && !isDraftDeal && !['closed', 'lost'].includes(transaction.status);
  const actionButtons = (
    <div style={styles.headerActions}>
      {isDraftDeal && (
        <button onClick={() => navigate(`/transactions/${id}/edit`)} style={{ ...styles.duplicateButton, background: '#D4AF37', color: '#1a1a1a', borderColor: '#B8962E' }} disabled={actionBusy} title="Finish this draft. Tasks and emails start only when you submit it.">
          Continue draft
        </button>
      )}
      {hasUcDraft && (
        <button onClick={() => navigate(`/transactions/${id}/under-contract`)} style={{ ...styles.duplicateButton, background: '#D4AF37', color: '#1a1a1a', borderColor: '#B8962E' }} disabled={actionBusy} title="Finish the under contract draft">
          Continue under contract draft
        </button>
      )}
      {showUcButton && !hasUcDraft && (
        <button onClick={() => navigate(`/transactions/${id}/under-contract`)} style={{ ...styles.duplicateButton, background: '#1a1a1a', color: '#ffffff' }} disabled={actionBusy} title="Open the Under Contract form">
          Under Contract Form
        </button>
      )}
      {['buyer', 'seller'].includes(String(transaction.type || '').toLowerCase()) && (
        <button onClick={() => navigate(`/transactions/${id}/edit`)} style={styles.duplicateButton} disabled={actionBusy} title="Edit any transaction detail">
          <Edit size={16} /> Edit details
        </button>
      )}
      {canUseQuickActions(userRole) && transaction.type === 'Seller' && !(transaction.status === 'live' || transaction.listingStatus === 'live' || transaction.status === 'listing-active') && (
        <button onClick={handleMarkActive} style={styles.duplicateButton} disabled={actionBusy} title="Set this listing to Active (Live)">
          Mark as Active
        </button>
      )}
      {canUseQuickActions(userRole) && transaction.type === 'Seller' && (
        <button onClick={handlePhotosIn} style={styles.duplicateButton} disabled={actionBusy} title="Add a Photos are in task for the Marketing Manager">
          Photos are in
        </button>
      )}
      <button onClick={handleDuplicate} style={styles.duplicateButton} disabled={actionBusy} title="Duplicate transaction (dates are cleared)">
        <Copy size={16} /> Duplicate
      </button>
      <button onClick={() => setActionMode('menu')} style={styles.removeButton} disabled={actionBusy} title="Delete or mark as lost">
        <Trash2 size={16} /> Delete / Lost
      </button>
    </div>
  );

  const savedNote = location.state && location.state.saved;
  const lostBanner = (
    <>
      {savedNote && (
        <div role="status" style={{ background: '#E8F5E9', border: '1px solid #2e7d32', color: '#1b5e20', borderRadius: 8, padding: '12px 16px', marginBottom: 16, fontWeight: 600 }}>
          ✓ Saved. {String(savedNote).replace(/^Saved\.\s*/, '')}
        </div>
      )}
      {(transaction.isDraft || transaction.status === 'draft') && (
        <div style={{ background: '#FFF8E1', border: '1px solid #D4AF37', borderRadius: 8, padding: '12px 16px', marginBottom: 16 }}>
          <strong>Draft:</strong> this transaction is not finished. No tasks or emails have been created. Use <strong>Continue draft</strong> to finish and submit it.
        </div>
      )}
      {transaction.status === 'lost' && (
        <div style={styles.lostBanner}>
          <strong>Lost:</strong> {transaction.lostReason}{transaction.lostDetail ? ` (${transaction.lostDetail})` : ''}
        </div>
      )}
    </>
  );

  const lostReady = !!lostChoice && (lostChoice !== 'Other' || !!lostNote.trim());

  const actionModal = actionMode ? (
    <div style={styles.overlay} onClick={closeActions}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        {actionMode === 'menu' && (
          <>
            <h3 style={styles.modalTitle}>What do you want to do with this transaction?</h3>
            <p style={styles.modalText}>Mark it lost if the deal fell through and you want to keep the record. Delete removes it for good.</p>
            <div style={styles.modalButtons}>
              <button onClick={() => setActionMode('lost')} style={styles.lostOption}>Mark as Lost</button>
              <button onClick={() => setActionMode('delete')} style={styles.deleteOption}>Delete permanently</button>
            </div>
            <button onClick={closeActions} style={styles.cancelLink}>Cancel</button>
          </>
        )}

        {actionMode === 'lost' && (
          <>
            <h3 style={styles.modalTitle}>Why was this deal lost?</h3>
            <p style={styles.modalText}>Choose one. This is required.</p>
            <div style={styles.radioList} role="radiogroup" aria-label="Reason lost">
              {LOST_REASONS.map((reason) => (
                <label key={reason} style={styles.radioRow}>
                  <input type="radio" name="lostReason" value={reason} checked={lostChoice === reason} onChange={() => setLostChoice(reason)} />
                  {reason}
                </label>
              ))}
            </div>
            <label style={styles.noteLabel}>
              {lostChoice === 'Other' ? 'Describe the reason (required)' : 'Details (optional)'}
              <textarea value={lostNote} onChange={(e) => setLostNote(e.target.value)} rows={3} style={styles.noteInput} />
            </label>
            <div style={styles.modalButtons}>
              <button onClick={handleMarkLost} disabled={!lostReady || actionBusy} style={{ ...styles.lostOption, opacity: lostReady ? 1 : 0.5 }}>
                Save as Lost
              </button>
            </div>
            <button onClick={() => setActionMode('menu')} style={styles.cancelLink}>Back</button>
          </>
        )}

        {actionMode === 'delete' && (
          <>
            <h3 style={styles.modalTitle}>Delete this transaction?</h3>
            <p style={styles.modalText}>This permanently removes the transaction and its tasks. It cannot be undone. If you only want to close it out, use Mark as Lost instead.</p>
            <div style={styles.modalButtons}>
              <button onClick={handleDelete} disabled={actionBusy} style={styles.deleteOption}>Yes, delete permanently</button>
            </div>
            <button onClick={closeActions} style={styles.cancelLink}>Cancel</button>
          </>
        )}
      </div>
    </div>
  ) : null;
  const createdDate = new Date(transaction.createdAt).toLocaleDateString();

  // Saves edited detail fields, then moves task due dates to match the deal's current dates.
  const saveDetailFields = async (updates) => {
    const owner = transaction.ownerUid || user.uid;
    await updateTransaction(owner, id, updates);
    // A live date entered on a seller deal makes the listing Live, starts the live tasks, and closes the MLS question.
    if (transaction.type === 'Seller' && updates.liveDate && transaction.listingStatus !== 'live') {
      await setListingLiveDate(id, owner, updates.liveDate, user.displayName || user.email || 'Unknown');
    }
    if (updates.status === 'closed' && transaction.status !== 'closed') {
      await releaseClosedTasks(id, updates.actualClosingDate || new Date().toISOString().slice(0, 10), user.uid, transaction.type, { justSoldPostcards: transaction.createJustSoldPostcards === true });
    }
    const fresh = await getTransaction(owner, id);
    setTransaction(prev => ({ ...prev, ...fresh }));
    await syncTaskDates(id, fresh);
    await syncTeamTaskDates(id, fresh);
    // Sends the professional staging email if staging was just set to professional. Failures never block the save.
    if (transaction.type === 'Seller') {
      try {
        await sendSellerCreatedEmails(id, fresh, owner, ['Auto Email: Professional Staging Request']);
      } catch (err) {
        console.error('Could not send staging email', err);
      }
    }
  };

  if (transaction.type === 'Buyer') {
    const fmtDate = (d) => {
      if (!d) return '';
      const dt = new Date(`${d}T00:00:00`);
      return isNaN(dt) ? d : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    };
    const money = (v) => (v ? `$${Number(String(v).replace(/[^0-9.]/g, '')).toLocaleString()}` : '');
    const yesNo = (v) => (v === 'Yes' ? 'Yes' : v === 'No' ? 'No' : '');
    const lender = transaction.lender || {};
    const tiles = [
      ['Email', transaction.email],
      ['Phone', transaction.phone],
      ['Under contract', fmtDate(transaction.contractDate)],
      ['Anticipated Closing', fmtDate(transaction.closingDate)],
      ['Payment', transaction.paymentType === 'Financing' && transaction.financingSource ? `Financing (${transaction.financingSource})` : transaction.paymentType],
      ['Lead Source', transaction.source === 'Other' ? transaction.sourceOther : transaction.source],
      ...(transaction.propertyStatus === 'new-construction' ? [['Builder', transaction.builderName], ['Community', transaction.communityName], ['Floorplan', transaction.floorplan], ['Base price', money(transaction.basePrice)], ['Builder pays closing costs', money(transaction.builderClosingCostAmount)], ['Commission based on', transaction.commissionBase], ['Reduce commission base by builder closing costs', transaction.reduceBaseByBuilderCC], ['Builder bonus', transaction.builderBonusType === 'percentage' ? `${transaction.builderBonusPercent}%` : transaction.builderBonusType === 'flat' ? money(transaction.builderBonusAmount) : transaction.builderBonusType === 'none' ? 'None' : '']] : []),
      ['Association approval', yesNo(transaction.associationApprovalRequired)],
      ['First earnest money', [money(transaction.firstEscrowAmount), fmtDate(transaction.firstEscrowDueDate)].filter(Boolean).join(' due ')],
      ['Second earnest money', [money(transaction.secondEscrowAmount), fmtDate(transaction.secondEscrowDueDate)].filter(Boolean).join(' due ')],
      ['Home inspection (due diligence)', fmtDate(transaction.homeInspectionDeadline)],
      ['Appraisal deadline', fmtDate(transaction.appraisalDeadline)],
      ['Home to sell contingency', fmtDate(transaction.homeSaleContingencyDate)],
      ...(transaction.paymentType === 'Financing' ? [['Financing deadline', fmtDate(transaction.financingDeadline)]] : []),
    ].filter(([, v]) => v);
    const lenderName = lender.companyName || [lender.firstName, lender.lastName].filter(Boolean).join(' ');
    const isNewConstruction = transaction.propertyStatus === 'new-construction';
    const otherSide = (isNewConstruction ? [
      ['Builder rep', [[transaction.builderRepFirstName, transaction.builderRepLastName].filter(Boolean).join(' '), transaction.builderRepPhone, transaction.builderRepEmail].filter(Boolean).join(' · ')],
    ] : [
      ['Seller\'s agent', [transaction.sellerAgent, transaction.sellerAgentPhone, transaction.sellerAgentEmail].filter(Boolean).join(' · ')],
      ['Seller\'s TC', [transaction.sellerAgentTCName, transaction.sellerAgentTCPhone, transaction.sellerAgentTCEmail].filter(Boolean).join(' · ')],
    ]).filter(([, v]) => v);

    const Tile = ({ label, value }) => (
      <div style={{ padding: '10px 12px', background: '#FFFFFF', borderRadius: 6, border: '1px solid #E1E4EA' }}>
        <div style={{ fontSize: 11, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8C6D1F', fontWeight: 700 }}>{label}</div>
        <div style={{ fontSize: 15, color: '#1a1a1a', marginTop: 4, wordBreak: 'break-word' }}>{value}</div>
      </div>
    );

    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <button onClick={() => navigate('/transactions')} style={styles.backButton}>
            <ArrowLeft size={20} /> Back
          </button>
          {isOwner ? actionButtons : null}
        </div>
        {actionModal}
        <div style={styles.mainContent}>
          {lostBanner}
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ ...styles.clientName, margin: 0 }}>{clientName}</h2>
            <div style={{ color: '#555', marginTop: 4 }}>{transaction.propertyAddress}{transaction.city ? `, ${transaction.city}` : ''}{transaction.state ? `, ${transaction.state}` : ''} {transaction.zipCode || ''}</div>
          </div>

          <BuyerDetailSections transaction={transaction} onSave={saveDetailFields} />

          {accessPanel}
          {!isLimitedAgent(userRole) && <AutomatedEmailsToggle transaction={transaction} transactionId={id} ownerUid={transaction.ownerUid || user.uid} onChange={(v) => setTransaction(prev => ({ ...prev, automatedEmailsEnabled: v }))} />}
          <DealColorPicker value={transaction.dealColor || ''} onChange={async (c) => {
            try {
              await updateTransaction(transaction.ownerUid || user.uid, id, { dealColor: c });
              setTransaction(prev => ({ ...prev, dealColor: c }));
            } catch (err) { console.error('Could not save deal color', err); }
          }} />
          <LinkedContacts contactIds={transaction.contactIds} />
        <SavedCmas who={{ transactionId: transaction.id || id, contactId: transaction.contactId, email: transaction.email, phone: transaction.phone }} />
          <DealDocumentsSection ownerUid={transaction.ownerUid || user.uid} transactionId={id} uploadedBy={user.displayName || user.email || ''} transactionType={transaction.type} underContract={!!transaction.underContract || ['pending', 'under-contract', 'closed'].includes(transaction.status)} />
          <div style={{ ...styles.section, marginBottom: 16 }}>
            <h3 style={styles.sectionTitle}>Tasks</h3>
            <TransactionCommunications transactionId={id} />
            {canUseCommsButtons(userRole) && <MessagesSection field="transactionId" value={id} title="Messages (emails & texts)" />}
<MarketingLinks transactionId={id} />
            <TransactionTasksList transactionId={id} deal={transaction} otherAgent={otherAgentOf(transaction)} dealAddress={transaction.propertyAddress} ownerUid={transaction.ownerUid || user.uid} listingAgent={{ firstName: transaction.homeSaleAgentFirstName, lastName: transaction.homeSaleAgentLastName, email: transaction.homeSaleAgentEmail, phone: transaction.homeSaleAgentPhone }} client={{ firstName: transaction.firstName, lastName: transaction.lastName, email: transaction.email, phone: transaction.phone }} />
          </div>

          {(userRole === 'Admin' || userRole === 'TC') && (
            <div style={{ ...styles.section, marginBottom: 16 }}>
              <h3 style={styles.sectionTitle}>Property and Service Information</h3>
              <PropertyInfoSection
                value={transaction.propertyInfo || {}}
                onChange={async (propertyInfo) => {
                  setTransaction(prev => ({ ...prev, propertyInfo }));
                  try { await updateTransaction(transaction.ownerUid || user.uid, id, { propertyInfo }); } catch (err) { console.error('Could not save property info', err); }
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate('/transactions')} style={styles.backButton}>
          <ArrowLeft size={20} /> Back
        </button>
        <h1 style={styles.title}>Transaction Details</h1>
        {isOwner ? actionButtons : null}
      </div>

      {actionModal}
      {/* Main Content */}
      <div style={styles.mainContent}>
        {lostBanner}
        {/* Header Info Card */}
        <div style={styles.headerCard}>
          <div>
            <h2 style={styles.clientName}>{clientName}</h2>
            <p style={styles.transactionType}>{transaction.type} Transaction</p>
            {transaction.buyerCategory && (
              <p style={styles.subType}>{transaction.buyerCategory}</p>
            )}
          </div>
          <div style={styles.dateBox}>
            <span style={styles.dateLabel}>Created</span>
            <span style={styles.dateValue}>{createdDate}</span>
          </div>
        </div>

        {/* Contact Information */}
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Contact Information</h3>
          {renderField('Email', transaction.email)}
          {renderField('Phone', transaction.phone)}
          {renderField('Current Address', transaction.currentAddress)}
          {(transaction.additionalContacts || []).filter(c => c && (c.firstName || c.lastName || c.email || c.phone)).map((c, i) => (
            <div key={c.id || i} style={{ padding: '6px 0', borderTop: '1px solid #EEE', fontSize: 14 }}>
              <strong>Contact {i + 2}:</strong> {[c.firstName, c.lastName].filter(Boolean).join(' ') || 'No name'}{c.role ? ` (${c.role})` : ''}
              {c.signer === false && <span style={{ marginLeft: 8, padding: '1px 8px', borderRadius: 10, background: '#F1F1F1', color: '#5B6270', fontSize: 12 }}>Not a signer</span>}
              {(c.email || c.phone) && <div style={{ color: '#5B6270', fontSize: 13 }}>{[c.email, c.phone].filter(Boolean).join(' · ')}</div>}
            </div>
          ))}
        </div>

        {/* Property Information (varies by type) */}
        {(transaction.type === 'Seller' || transaction.propertyAddress) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Property Information</h3>
            {renderField('Property Address', transaction.propertyAddress)}
            {renderField('Property Type', transaction.propertyType)}
            {renderField('Bedrooms', transaction.bedroomCount)}
            {renderField('Bathrooms', transaction.bathroomCount)}
            {renderField('Square Footage', transaction.squareFootage)}
            {transaction.cddAnnual != null && transaction.cddAnnual !== '' && renderField('CDD (annual)', `$${Number(transaction.cddAnnual).toLocaleString('en-US')}`)}
            {renderField('Year Built', transaction.yearBuilt)}
          </div>
        )}

        {/* Seller-Specific Information */}
        {transaction.type === 'Seller' && transaction.underContract && (
          <UnderContractSummary uc={transaction.underContract} onEdit={transaction.status === 'lost' ? null : () => navigate(`/transactions/${id}/under-contract`)} />
        )}
        {transaction.type === 'Seller' && (
          <SellerDetailSections transaction={transaction} onSave={saveDetailFields} />
        )}

        {/* Buyer-Specific Information */}
        {transaction.type === 'Buyer' && (
          <>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Buyer Profile</h3>
              {renderField('Buyer Type', transaction.buyerType)}
              {renderField('Relocating From', transaction.relocatingFrom)}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Financing</h3>
              {renderField('Pre-Approval Amount', transaction.preApprovalAmount)}
              {renderField('Down Payment %', transaction.downPayment)}
              {renderField('Loan Amount', transaction.loanAmount)}
              {renderField('Closing Timeline', transaction.closingTimeline)}
            </div>

            {transaction.purchaseCategory === 'resale' ? (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>Resale Preferences</h3>
                {renderField('Property Type', transaction.propertyType)}
                {renderField('Desired Price', transaction.desiredPrice)}
                {renderField('Desired Locations', transaction.desiredLocation)}
                {renderField('Bedroom Preference', transaction.bedroomPreference)}
                {renderField('Bathroom Preference', transaction.bathroomPreference)}
                {renderField('Home Inspection Required', transaction.homeInspectionRequired)}
                {renderField('Appraisal Contingency', transaction.appraisalContingency)}
                {renderField('Current Home Sale Status', transaction.currentHomeSaleStatus)}
                {renderField('Offer Strategy', transaction.offerStrategy)}
              </div>
            ) : (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>New Construction Preferences</h3>
                {renderField('Builder Preference', transaction.builderPreference)}
                {renderField('Community Preference', transaction.communityPreference)}
                {renderField('Desired Price', transaction.desiredPrice)}
                {renderField('Desired Locations', transaction.desiredLocation)}
                {renderField('Move-In Timeline', transaction.moveInTimeline)}
                {renderField('Model/Floor Plan', transaction.modelFloorplanPreference)}
                {renderField('Lot Preferences', transaction.lotPreferences)}
                {renderField('Builder Incentives Interest', transaction.builderIncentivesInterest)}
                {renderField('HOA Concerns', transaction.hoaConcerns)}
                {renderField('Warranty Questions', transaction.warrantyQuestions)}
              </div>
            )}
          </>
        )}

        {/* Landlord-Specific Information */}
        {transaction.type === 'Landlord' && (
          <>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Rental Information</h3>
              {renderField('Monthly Rent', transaction.rentAmount)}
              {renderField('Desired Lease Type', transaction.desiredLeaseType)}
              {renderField('Available Date', transaction.availableDate)}
              {renderField('Furniture Included', transaction.furnitureIncluded)}
              {renderField('Utilities Included', transaction.utilitiesIncluded)}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Tenant Preferences</h3>
              {renderField('Allow Pets', transaction.allowPets)}
              {renderField('Minimum Income Requirement', transaction.minIncomeRequirement)}
              {renderField('Minimum Credit Score', transaction.creditScoreRequirement)}
              {renderField('Lease Terms', transaction.leaseTerms)}
            </div>
          </>
        )}

        {/* Tenant-Specific Information */}
        {transaction.type === 'Tenant' && (
          <>
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Move Information</h3>
              {renderField('Desired Location', transaction.desiredLocation)}
              {renderField('Desired Move-In Date', transaction.moveInDate)}
              {renderField('Lease Length Preference', transaction.leaseLength)}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Financial Information</h3>
              {renderField('Monthly Income', transaction.income)}
              {renderField('Income Source', transaction.incomeSource)}
              {renderField('Employer Name', transaction.employerName)}
              {renderField('Employment Status', transaction.employmentStatus)}
            </div>

            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>Housing Preferences</h3>
              {renderField('Property Type', transaction.propertyType)}
              {renderField('Bedroom Preference', transaction.bedroomPreference)}
              {renderField('Bathroom Preference', transaction.bathroomPreference)}
              {renderField('Budget Range', transaction.budgetRange)}
              {renderField('Pets', transaction.petInfo)}
              {transaction.petInfo !== 'no' && renderField('Pet Details', transaction.petDetails)}
            </div>
          </>
        )}

        {/* Additional Notes */}
        {(transaction.notes || transaction.specialRequirements || transaction.agentNotes) && (
          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Additional Information</h3>
            {renderField('Notes', transaction.notes)}
            {renderField('Special Requirements', transaction.specialRequirements)}
            {renderField('Agent Notes', transaction.agentNotes)}
          </div>
        )}

        {/* Transaction Tasks */}
        {(userRole === 'Admin' || userRole === 'TC') && (
          <PropertyInfoSection
            value={transaction?.propertyInfo || {}}
            onChange={async (propertyInfo) => {
              setTransaction(prev => ({ ...prev, propertyInfo }));
              try { await updateTransaction(transaction.ownerUid || user.uid, id, { propertyInfo }); } catch (err) { console.error('Could not save property info', err); }
            }}
          />
        )}
        <TransactionCommunications transactionId={id} />
<MarketingLinks transactionId={id} />
        {accessPanel}
        {!isLimitedAgent(userRole) && <AutomatedEmailsToggle transaction={transaction} transactionId={id} ownerUid={transaction.ownerUid || user.uid} onChange={(v) => setTransaction(prev => ({ ...prev, automatedEmailsEnabled: v }))} />}
        <DealColorPicker value={transaction.dealColor || ''} onChange={async (c) => {
          try {
            await updateTransaction(transaction.ownerUid || user.uid, id, { dealColor: c });
            setTransaction(prev => ({ ...prev, dealColor: c }));
          } catch (err) { console.error('Could not save deal color', err); }
        }} />
        <LinkedContacts contactIds={transaction.contactIds} />
        <SavedCmas who={{ transactionId: transaction.id || id, contactId: transaction.contactId, email: transaction.email, phone: transaction.phone }} />
        <DealDocumentsSection ownerUid={transaction.ownerUid || user.uid} transactionId={id} uploadedBy={user.displayName || user.email || ''} transactionType={transaction.type} underContract={!!transaction.underContract || ['pending', 'under-contract', 'closed'].includes(transaction.status)} />
        <TransactionTasksList transactionId={id} deal={transaction} otherAgent={otherAgentOf(transaction)} dealAddress={transaction.propertyAddress} ownerUid={transaction.ownerUid || user.uid} listingAgent={{ firstName: transaction.homeSaleAgentFirstName, lastName: transaction.homeSaleAgentLastName, email: transaction.homeSaleAgentEmail, phone: transaction.homeSaleAgentPhone }} client={{ firstName: transaction.firstName, lastName: transaction.lastName, email: transaction.email, phone: transaction.phone }} />
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FFFFFF',
    fontFamily: 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  },
  header: {
    backgroundColor: '#F5F5F5',
    borderBottom: '1px solid #E0E0E0',
    padding: '20px 40px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#666',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: '4px',
    transition: 'all 0.3s ease'
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#000',
    margin: '0'
  },
  headerActions: {
    display: 'flex',
    gap: '8px'
  },
  duplicateButton: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', backgroundColor: '#FFFFFF', color: '#1a1a1a', border: '1px solid #B8962E', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  removeButton: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', backgroundColor: '#FFFFFF', color: '#8A1F1F', border: '1px solid #D64545', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  lostBanner: { padding: '10px 14px', marginBottom: 16, borderRadius: 6, background: '#FFF7E6', border: '1px solid #E0A526', color: '#6B4A00', fontSize: 14 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(20, 24, 33, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 },
  modal: { background: '#FFFFFF', borderRadius: 12, padding: 24, width: '100%', maxWidth: 440, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.25)' },
  modalTitle: { margin: '0 0 8px 0', fontSize: 18, color: '#1A1A1A' },
  modalText: { margin: '0 0 16px 0', fontSize: 14, color: '#5B6270', lineHeight: 1.5 },
  modalButtons: { display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 },
  lostOption: { padding: '10px 14px', background: 'linear-gradient(135deg, #f5e09a 0%, #e3c35f 45%, #b8962e 100%)', border: '1px solid #b8962e', borderRadius: 8, color: '#1a1400', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  deleteOption: { padding: '10px 14px', background: '#D64545', border: '1px solid #B93838', borderRadius: 8, color: '#FFFFFF', fontWeight: 700, cursor: 'pointer', fontSize: 14 },
  cancelLink: { marginTop: 12, background: 'none', border: 'none', color: '#5B6270', cursor: 'pointer', fontSize: 13, textDecoration: 'underline' },
  radioList: { display: 'flex', flexDirection: 'column', gap: 8 },
  radioRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#1A1A1A', cursor: 'pointer' },
  noteLabel: { display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14, fontSize: 13, color: '#5B6270', fontWeight: 600 },
  noteInput: { padding: 8, border: '1px solid #D5D9E0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', resize: 'vertical', color: '#1A1A1A' },
  deleteButton: {
    backgroundColor: '#f44336',
    color: '#FFFFFF',
    border: 'none',
    padding: '8px 12px',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    transition: 'all 0.3s ease'
  },
  mainContent: {
    padding: '40px',
    maxWidth: '1000px',
    margin: '0 auto'
  },
  loadingText: {
    fontSize: '16px',
    color: '#666',
    textAlign: 'center'
  },
  errorText: {
    fontSize: '16px',
    color: '#d32f2f',
    textAlign: 'center'
  },
  headerCard: {
    backgroundColor: '#F5F5F5',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start'
  },
  clientName: {
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px 0',
    color: '#000'
  },
  transactionType: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#D4AF37',
    margin: '0'
  },
  subType: {
    fontSize: '14px',
    color: '#666',
    margin: '4px 0 0 0'
  },
  dateBox: {
    textAlign: 'center',
    padding: '16px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #DDD',
    borderRadius: '4px'
  },
  dateLabel: {
    display: 'block',
    fontSize: '12px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  dateValue: {
    display: 'block',
    fontSize: '18px',
    fontWeight: '600',
    color: '#000',
    marginTop: '4px'
  },
  section: {
    backgroundColor: '#F9F9F9',
    border: '1px solid #E0E0E0',
    borderRadius: '8px',
    padding: '24px',
    marginBottom: '24px'
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#000',
    margin: '0 0 16px 0',
    paddingBottom: '12px',
    borderBottom: '2px solid #D4AF37'
  },
  fieldRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingBottom: '12px',
    marginBottom: '12px',
    borderBottom: '1px solid #E0E0E0'
  },
  fieldLabel: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    minWidth: '200px'
  },
  fieldValue: {
    fontSize: '14px',
    color: '#333',
    textAlign: 'right',
    flex: 1,
    paddingLeft: '16px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word'
  }
};
